import React, { useRef, useState } from 'react';

import PushPinRoundedIcon from '@mui/icons-material/PushPinRounded';
import {
  Box,
  Card,
  CardContent,
  Divider,
  Grid,
  IconButton,
  List,
  ListItemButton,
  ListItemText,
  Skeleton,
  Tooltip,
  Typography,
} from '@mui/material';
import { compact, filter, first, flatMap, keys, map, size, some } from 'es-toolkit/compat';

import { studyDashboardHeaderHeight, studyDashboardTabsHeight } from 'components/atoms/FlexContainers/FlexContainers';
import { pageHeaderHeight } from 'components/PageHeader';
import AnnotationAssignmentView from 'components/StudyDashboard/Tasks/AnnotationAssignmentView';
import { AnnotationAssignment } from 'interfaces/annotation';
import { filterSearchOptions } from 'services/matchSort';
import { getTotalAnnotationsCount } from './AnnotationAssignmentTable.utils';

export type AssignmentFilterMode = 'all' | 'study' | 'global';

const assignmentScrollTopGap = 24;
const stickyDashboardChromeHeight = studyDashboardHeaderHeight + studyDashboardTabsHeight + 1;
export const assignmentScrollOffset = stickyDashboardChromeHeight + assignmentScrollTopGap;
export const assignmentDetailsBottomPadding = `max(24px, calc(100vh - ${pageHeaderHeight + assignmentScrollOffset}px))`;

interface AnnotationAssignmentQuickNavigatorProps {
  studyId?: string;
  annotationAssignments?: AnnotationAssignment[];
  selectedAssignmentId?: number | null;
  isLoading: boolean;
  fetchOnlySlidesOfStudy: boolean;
  withLegacy: boolean;
  getStudyId?: (annotationAssignment: AnnotationAssignment) => string | undefined;
  onSelectAssignment?: (annotationAssignmentId: number) => void;
  pinnedAssignmentIds?: number[];
  onTogglePinAssignment?: (annotationAssignment: AnnotationAssignment) => void;
  listTitle?: string;
  listDescription?: string;
  searchableAssignments?: SearchableAnnotationAssignment[] | undefined;
  renderAllAssignmentDetails?: boolean;
  listLgColumns?: number;
  detailLgColumns?: number;
  listMaxHeight?: string | { lg: string };
  stickyTop?: number | { lg: number };
  detailsBottomPadding?: string | number | { xs: number; lg: string | number };
  scrollOffset?: number;
}

interface SearchableAnnotationAssignment {
  annotationAssignment: AnnotationAssignment;
  searchText: string;
}

const AnnotationAssignmentQuickNavigator: React.FC<AnnotationAssignmentQuickNavigatorProps> = ({
  studyId,
  annotationAssignments,
  selectedAssignmentId,
  isLoading,
  fetchOnlySlidesOfStudy,
  withLegacy,
  getStudyId,
  onSelectAssignment,
  pinnedAssignmentIds = [],
  onTogglePinAssignment,
  listTitle = 'Assignments',
  listDescription = 'Open the assignment you want to review or edit.',
  renderAllAssignmentDetails = true,
  listLgColumns = 3,
  detailLgColumns = 9,
  listMaxHeight = { lg: 'calc(100vh - 360px)' },
  stickyTop = { lg: 24 },
  detailsBottomPadding = { xs: 3, lg: assignmentDetailsBottomPadding },
  scrollOffset = assignmentScrollOffset,
}) => {
  const [internalSelectedAssignmentId, setInternalSelectedAssignmentId] = useState<number | null>(null);
  const fallbackSelectedAssignmentId = first(annotationAssignments)?.annotationAssignmentId ?? null;
  const selectedAssignmentIsVisible = some(
    annotationAssignments,
    ({ annotationAssignmentId }) => annotationAssignmentId === (selectedAssignmentId ?? internalSelectedAssignmentId)
  );
  const activeAssignmentId = selectedAssignmentIsVisible
    ? selectedAssignmentId ?? internalSelectedAssignmentId
    : fallbackSelectedAssignmentId;
  const visibleAssignmentDetails = renderAllAssignmentDetails
    ? annotationAssignments
    : filter(annotationAssignments, ({ annotationAssignmentId }) => annotationAssignmentId === activeAssignmentId);
  const sectionRefs = useRef<{ [key: number]: HTMLDivElement | null }>({});

  const scrollToSection = (annotationAssignmentId: number) => {
    setInternalSelectedAssignmentId(annotationAssignmentId);
    onSelectAssignment?.(annotationAssignmentId);

    if (!renderAllAssignmentDetails) {
      return;
    }

    const sectionElement = sectionRefs.current[annotationAssignmentId];

    if (!sectionElement) {
      return;
    }

    const scrollContainer = getNearestVerticalScrollContainer(sectionElement);

    if (!scrollContainer) {
      sectionElement.scrollIntoView({
        behavior: 'smooth',
        block: 'start',
      });

      return;
    }

    const containerRect = scrollContainer.getBoundingClientRect();
    const sectionRect = sectionElement.getBoundingClientRect();
    const nextScrollTop = scrollContainer.scrollTop + sectionRect.top - containerRect.top - scrollOffset;
    const maxScrollTop = scrollContainer.scrollHeight - scrollContainer.clientHeight;

    scrollContainer.scrollTo({
      top: Math.min(Math.max(nextScrollTop, 0), maxScrollTop),
      behavior: 'smooth',
    });
  };

  return (
    <>
      <Grid item xs={12} lg={listLgColumns}>
        <Card sx={{ position: { lg: 'sticky' }, top: stickyTop }}>
          <CardContent sx={{ pb: 1.5 }}>
            <Typography variant="h4">{listTitle}</Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mt: 0.75 }}>
              {listDescription}
            </Typography>
          </CardContent>
          <Divider />
          <Box sx={{ maxHeight: listMaxHeight, overflowY: 'auto' }}>
            {isLoading ? (
              <Box sx={{ p: 2 }}>
                <ListSkeleton />
              </Box>
            ) : (
              <List component="nav" disablePadding>
                {map(annotationAssignments, (annotationAssignment: AnnotationAssignment) => {
                  const assignmentDisplayName = getAssignmentDisplayName(annotationAssignment);
                  const isSelected = annotationAssignment.annotationAssignmentId === activeAssignmentId;
                  const isPinned = pinnedAssignmentIds.includes(annotationAssignment.annotationAssignmentId);

                  return (
                    <Tooltip
                      key={annotationAssignment.annotationAssignmentId}
                      title={assignmentDisplayName}
                      arrow
                      enterDelay={750}
                      enterNextDelay={750}
                    >
                      <ListItemButton
                        component="div"
                        selected={isSelected}
                        onClick={() => scrollToSection(annotationAssignment.annotationAssignmentId)}
                        sx={{
                          alignItems: 'flex-start',
                          px: 2,
                          py: 1.5,
                          borderLeft: '3px solid',
                          borderLeftColor: isSelected ? 'primary.main' : 'transparent',
                          '&.Mui-selected, &.Mui-selected:hover': {
                            backgroundColor: 'action.selected',
                          },
                        }}
                      >
                        <Box sx={{ display: 'flex', alignItems: 'flex-start', width: '100%', gap: 1 }}>
                          <ListItemText
                            primary={assignmentDisplayName}
                            secondary={getAssignmentSummaryLabel(annotationAssignment)}
                            primaryTypographyProps={{
                              style: {
                                overflow: 'hidden',
                                whiteSpace: 'nowrap',
                                textOverflow: 'ellipsis',
                              },
                            }}
                            secondaryTypographyProps={{
                              color: 'text.secondary',
                            }}
                            sx={{ minWidth: 0, my: 0 }}
                          />
                          {onTogglePinAssignment ? (
                            <IconButton
                              size="small"
                              color={isPinned ? 'primary' : 'default'}
                              aria-label={`${isPinned ? 'Unpin' : 'Pin'} assignment ${assignmentDisplayName}`}
                              onClick={(event) => {
                                event.stopPropagation();
                                onTogglePinAssignment(annotationAssignment);
                              }}
                              sx={{ mt: -0.5 }}
                            >
                              <PushPinRoundedIcon fontSize="small" />
                            </IconButton>
                          ) : null}
                        </Box>
                      </ListItemButton>
                    </Tooltip>
                  );
                })}
              </List>
            )}
          </Box>
        </Card>
      </Grid>

      <Grid item xs={12} lg={detailLgColumns}>
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            gap: 3,
            pb: detailsBottomPadding,
          }}
        >
          {isLoading ? (
            <AssignmentsTablesSkeleton />
          ) : (
            map(visibleAssignmentDetails, (annotationAssignment) => {
              const annotationAssignmentStudyId =
                getStudyId?.(annotationAssignment) || annotationAssignment.studyId || studyId || '';

              return (
                <AnnotationAssignmentView
                  key={annotationAssignment.annotationAssignmentId}
                  studyId={annotationAssignmentStudyId}
                  annotationAssignment={annotationAssignment}
                  fetchOnlySlidesOfStudy={fetchOnlySlidesOfStudy}
                  withLegacy={withLegacy}
                  scrollAnchorRef={(element: HTMLDivElement | null) =>
                    (sectionRefs.current[annotationAssignment.annotationAssignmentId] = element)
                  }
                />
              );
            })
          )}
        </Box>
      </Grid>
    </>
  );
};

export function getAssignmentDisplayName(annotationAssignment: AnnotationAssignment) {
  return `${annotationAssignment.annotationAssignmentId}- ${annotationAssignment.name}`;
}

export function getAssignmentSummaryLabel(annotationAssignment: AnnotationAssignment) {
  const assignmentClassesCount = size(first(annotationAssignment.todos)?.options ?? []);
  const assignmentSlidesCount =
    annotationAssignment.slideMetadataSummary?.totalSlideCount ?? size(annotationAssignment.annotations ?? []);
  const annotatedSlidesCount =
    annotationAssignment.slideMetadataSummary?.annotatedSlideCount ??
    size(
      filter(
        annotationAssignment.annotations ?? [],
        (annotation) =>
          getTotalAnnotationsCount(annotation.annotationSummary) > 0 ||
          getTotalAnnotationsCount(annotation.draftAnnotationSummary) > 0
      )
    );

  return `${assignmentClassesCount} classes • ${annotatedSlidesCount}/${assignmentSlidesCount} annotated slides`;
}

export function matchesAssignmentFilters(
  annotationAssignment: AnnotationAssignment,
  searchQuery: string,
  assignmentFilterMode: AssignmentFilterMode
) {
  return some(filterAnnotationAssignments([annotationAssignment], searchQuery, assignmentFilterMode));
}

export function buildSearchableAnnotationAssignments(annotationAssignments: AnnotationAssignment[] = []) {
  return map(
    annotationAssignments,
    (annotationAssignment): SearchableAnnotationAssignment => ({
      annotationAssignment,
      searchText: getAssignmentSearchText(annotationAssignment),
    })
  );
}

export function filterSearchableAnnotationAssignments(
  searchableAssignments: SearchableAnnotationAssignment[] | undefined,
  searchQuery: string,
  assignmentFilterMode: AssignmentFilterMode
) {
  const filterModeMatches = filter(searchableAssignments || [], ({ annotationAssignment }) =>
    matchesAssignmentFilterMode(annotationAssignment, assignmentFilterMode)
  );
  const trimmedSearchQuery = searchQuery.trim();

  if (!trimmedSearchQuery) {
    return map(filterModeMatches, ({ annotationAssignment }) => annotationAssignment);
  }

  return map(
    filterSearchOptions(['searchText'], filterModeMatches, trimmedSearchQuery),
    ({ annotationAssignment }) => annotationAssignment
  );
}

export function filterAnnotationAssignments(
  annotationAssignments: AnnotationAssignment[] | undefined,
  searchQuery: string,
  assignmentFilterMode: AssignmentFilterMode
) {
  return filterSearchableAnnotationAssignments(
    buildSearchableAnnotationAssignments(annotationAssignments),
    searchQuery,
    assignmentFilterMode
  );
}

function matchesAssignmentFilterMode(
  annotationAssignment: AnnotationAssignment,
  assignmentFilterMode: AssignmentFilterMode
) {
  const matchesFilterMode =
    assignmentFilterMode === 'all' ||
    (assignmentFilterMode === 'global' ? annotationAssignment.isGlobal : !annotationAssignment.isGlobal);

  return matchesFilterMode;
}

export function getAssignmentSearchText(annotationAssignment: AnnotationAssignment) {
  const todoOptions = flatMap(annotationAssignment.todos ?? [], (todo) => todo.options ?? []);

  return compact([
    annotationAssignment.annotationAssignmentId.toString(),
    annotationAssignment.name,
    annotationAssignment.description,
    annotationAssignment.createdBy,
    ...(annotationAssignment.tags ?? []),
    ...flatMap(annotationAssignment.slideMetadataSummary?.stainTypeCounts ?? [], ({ value }) => value),
    ...flatMap(annotationAssignment.slideMetadataSummary?.biopsySiteCounts ?? [], ({ value }) => value),
    ...flatMap(annotationAssignment.todos ?? [], (todo) => [todo.todo, todo.type]),
    ...flatMap(todoOptions, (option) => [
      option.displayName,
      option.name,
      option.className,
      option.shapeType,
      option.originPanelId,
      option.isMarkerAnnotation ? 'marker' : 'class',
      ...keys(option.markerPositivity ?? {}),
    ]),
  ])
    .join(' ')
    .toLowerCase();
}

function getNearestVerticalScrollContainer(element: HTMLElement | null) {
  if (!element || typeof window === 'undefined') {
    return null;
  }

  let currentElement = element.parentElement;

  while (currentElement) {
    const { overflowY } = window.getComputedStyle(currentElement);
    const isScrollable = ['auto', 'overlay', 'scroll'].includes(overflowY);

    if (isScrollable && currentElement.scrollHeight > currentElement.clientHeight) {
      return currentElement;
    }

    currentElement = currentElement.parentElement;
  }

  return null;
}

function ListSkeleton() {
  return (
    <Box>
      <Skeleton variant="text" sx={{ fontSize: '1rem', width: '75%' }} />
      <Skeleton variant="text" sx={{ fontSize: '0.875rem', width: '45%', mb: 1.5 }} />
      <Skeleton variant="text" sx={{ fontSize: '1rem', width: '70%' }} />
      <Skeleton variant="text" sx={{ fontSize: '0.875rem', width: '40%', mb: 1.5 }} />
      <Skeleton variant="text" sx={{ fontSize: '1rem', width: '65%' }} />
      <Skeleton variant="text" sx={{ fontSize: '0.875rem', width: '35%' }} />
    </Box>
  );
}

function AssignmentsTablesSkeleton() {
  return (
    <>
      {[1, 2].map((skeletonIndex) => (
        <Box key={skeletonIndex}>
          <Card>
            <CardContent>
              <Skeleton variant="text" sx={{ fontSize: '1rem', width: '50%' }} />
              <Skeleton variant="text" sx={{ fontSize: '0.875rem', width: '35%', mb: 2 }} />
              <Skeleton variant="rectangular" width="100%" height={240} />
            </CardContent>
          </Card>
        </Box>
      ))}
    </>
  );
}

export default AnnotationAssignmentQuickNavigator;
